import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { ShoppingBag, User, Menu, X } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";

export default function Header() {
  const { count, setOpen } = useCart();
  const { user, logout } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [mobile, setMobile] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 backdrop-blur-xl transition-colors duration-300 ${
        scrolled ? "bg-[#FAF9F6]/85 border-b border-[#E5E5E5]" : "bg-[#FAF9F6]/60"
      }`}
    >
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12 h-24 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-4">
          <img
            src="/theratec-logo.png"
            alt="TheraTec"
            className="h-14 lg:h-16 w-auto object-contain"
          />
          <span className="overline hidden md:inline">High End Wellness Tech</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-10">
          <Link to="/shop" data-testid="nav-shop" className="tt-nav-link text-sm font-medium tracking-wide hover:text-[#5C5C5C] transition-colors duration-300">Shop</Link>
          <Link to="/hyperbaric" data-testid="nav-hyperbaric" className="tt-nav-link text-sm font-medium tracking-wide hover:text-[#5C5C5C] transition-colors duration-300">Hyperbaric</Link>
          <Link to="/shop?category=Red Light Therapy" data-testid="nav-red-light" className="tt-nav-link text-sm font-medium tracking-wide hover:text-[#5C5C5C] transition-colors duration-300">Red Light</Link>
          <Link to="/shop?category=Detox Systems" data-testid="nav-detox" className="tt-nav-link text-sm font-medium tracking-wide hover:text-[#5C5C5C] transition-colors duration-300">Detox</Link>
          <Link to="/integration" data-testid="nav-integration" className="tt-nav-link text-sm font-medium tracking-wide hover:text-[#5C5C5C] transition-colors duration-300">Integration</Link>
        </nav>

        <div className="flex items-center gap-5">
          {user ? (
            <div className="hidden sm:flex items-center gap-4">
              <Link to="/account" data-testid="account-link" className="text-sm hover:text-[#5C5C5C] transition-colors duration-300 flex items-center gap-2">
                <User size={16} strokeWidth={1.25} /> {user.business_name || user.email.split("@")[0]}
              </Link>
              <button data-testid="logout-btn" onClick={() => { logout(); nav("/"); }} className="overline hover:text-[#1C1C1C] transition-colors duration-300">Sign out</button>
            </div>
          ) : (
            <Link to="/login" data-testid="signin-link" className="hidden sm:inline-flex items-center gap-2 text-sm hover:text-[#5C5C5C] transition-colors duration-300">
              <User size={16} strokeWidth={1.25} /> Sign in
            </Link>
          )}

          <button
            data-testid="cart-trigger"
            onClick={() => setOpen(true)}
            className="relative flex items-center gap-2 group"
          >
            <ShoppingBag size={18} strokeWidth={1.25} className="group-hover:-rotate-6 transition-transform duration-300" />
            <span className="overline hidden sm:inline">Cart</span>
            {count > 0 && (
              <span data-testid="cart-count-badge" className="absolute -top-2 -right-3 bg-[#1C1C1C] text-[#FAF9F6] text-[10px] w-5 h-5 flex items-center justify-center rounded-full">{count}</span>
            )}
          </button>

          <button data-testid="mobile-menu-btn" onClick={() => setMobile(!mobile)} className="lg:hidden">
            {mobile ? <X size={20} strokeWidth={1.25} /> : <Menu size={20} strokeWidth={1.25} />}
          </button>
        </div>
      </div>

      {mobile && (
        <div data-testid="mobile-menu" className="lg:hidden border-t border-[#E5E5E5] bg-[#FAF9F6] px-6 py-6 flex flex-col gap-4">
          <Link to="/shop" onClick={() => setMobile(false)} className="text-lg font-serif-editorial">Shop</Link>
          <Link to="/integration" onClick={() => setMobile(false)} className="text-lg font-serif-editorial">Integration</Link>
          {user ? (
            <Link to="/account" onClick={() => setMobile(false)} className="text-lg font-serif-editorial">Account</Link>
          ) : (
            <Link to="/login" onClick={() => setMobile(false)} className="text-lg font-serif-editorial">Sign in</Link>
          )}
        </div>
      )}
    </header>
  );
}
